import { BOUNTY_MARK } from './bounties/mark';
import { isToday, isYesterday, isWithinInterval, subDays } from 'date-fns';
import { giftTermLabel } from './referral';
import {
  SUGGESTED_CONNECTION_EMAIL_FRAMINGS,
  suggestedConnectionAxisFromMetadata,
} from './suggested-connection-reason';

export interface NotificationActorBadge {
  id: string;
  contestWins?: Array<{ place: number; contestName: string; contestThumbnailUrl?: string | null }> | null;
  badge: {
    id: string;
    name: string;
    color: string;
    icon: string;
    hoverText?: string | null;
    url?: string | null;
    iconOnly?: boolean | null;
    thumbnailUrl?: string | null;
    ctaText?: string | null;
    ctaUrl?: string | null;
    ctaBgColor?: string | null;
    ctaTextColor?: string | null;
  };
}

export interface NotificationActor {
  asfId: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  profile?: {
    slug?: string;
    avatarUrl?: string;
    displayName?: string;
    isHumanVerified?: boolean | null;
    badges?: NotificationActorBadge[] | null;
  };
}

export interface NotificationUser {
  asfId: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  profile?: {
    slug?: string;
  };
}

export interface Notification {
  id: string;
  type: string;
  targetType?: string | null;
  targetId?: string | null;
  isRead: boolean;

  metadata?: Record<string, any> | null;
  createdAt: string;
  // Optional for backwards-compat with cached payloads that pre-date the
  // `updatedAt` column; consumers should prefer it when present and fall
  // back to `createdAt` otherwise.
  updatedAt?: string | null;
  /**
   * Points awarded by this notification's underlying ledger row (only set
   * for LIKE / COMMENT_LIKE / COMMENT notifications under the reputation
   * Phase 1 spec, #2530). Server-side join from the notification's
   * `(actorUserId, sourceType, sourceId)` triple; absent for FOLLOW,
   * COMMUNITY_REPLY, and all non-rep notification types.
   */
  pointsAwarded?: number | null;
  user: NotificationUser;
  actor?: NotificationActor | null;
  /**
   * The several people a single row is about, when `actor` cannot express it.
   *
   * `RECOMMENDED_CONNECTIONS` is one row naming N suggested artists in
   * `metadata.recommendationIds`; there is no one actor. Populated by the caller
   * that hydrates those ids into profiles, so surfaces which show faces can show
   * real ones instead of nothing.
   */
  relatedActors?: NotificationActor[] | null;
}

export interface NotificationGroups {
  today: Notification[];
  yesterday: Notification[];
  last7Days: Notification[];
  last30Days: Notification[];
  /**
   * Everything older than 30 days. Notifications are retained indefinitely and
   * surfaces that page backwards will hand us those rows, so they need a bucket
   * to land in — without one they are fetched and then rendered nowhere.
   */
  older: Notification[];
}

/**
 * What a member crossed, in their own terms.
 *
 * Written from the threshold rather than a stored sentence so the copy can change
 * without a migration, and so a row written months ago reads the way today's product
 * words it.
 */
/** "New reward unlocked: Facebook Glow-Up 🎁", naming the most valuable when one entry unlocked several. */
function rewardUnlockedLine(metadata?: Record<string, any> | null): string {
  const unlocked = Array.isArray(metadata?.unlocked) ? metadata.unlocked : [];
  const top = String(unlocked[0]?.title ?? '');
  if (!top) return 'You unlocked a new reward 🎁';
  if (unlocked.length === 1) return `New reward unlocked: ${top} 🎁`;
  const others = unlocked.length - 1;
  return `You unlocked ${top} and ${others} more ${others === 1 ? 'reward' : 'rewards'} 🎁`;
}

export function milestoneLine(metadata?: Record<string, any> | null): string {
  const family = String(metadata?.family ?? '');
  const threshold = Number(metadata?.threshold ?? 0);

  if (family === 'points') return `You've earned ${threshold.toLocaleString()} points 🎉`;
  if (family === 'reward') return rewardUnlockedLine(metadata);
  if (family === 'followers') {
    return threshold === 1
      ? 'You have your first follower 🎉'
      : `${threshold.toLocaleString()} artists follow you now 🎉`;
  }
  if (family === 'anniversary') {
    return threshold === 1 ? "It's been a year on ArtHelper 🎉" : `${threshold} years on ArtHelper 🎉`;
  }
  if (family === 'likes') return `Your art has ${threshold.toLocaleString()} likes 🎉`;
  if (family === 'views') return `Your art passed ${threshold.toLocaleString()} views 🎉`;
  // Creative firsts name the thing that was made, because that is what makes it feel
  // like an achievement rather than a counter ticking over.
  const subject = String(metadata?.subject ?? 'something new');
  // A critique is given TO someone else's piece — "is up" reads like they posted their
  // own, so name the act.
  if (subject === 'critique') return 'You gave your first critique 🎉';
  return `Your first ${subject} is up 🎉`;
}

/** Third-person reason a connection would tap Congratulate. */
function connectionMilestoneReason(metadata?: Record<string, any> | null): string {
  const family = String(metadata?.family ?? '');
  const threshold = Number(metadata?.threshold ?? 0);

  if (family === 'points') return `earned ${threshold.toLocaleString()} points 🎉`;
  if (family === 'followers') {
    return threshold === 1
      ? 'has their first follower 🎉'
      : `is followed by ${threshold.toLocaleString()} artists now 🎉`;
  }
  if (family === 'anniversary') {
    return threshold === 1 ? 'has been on ArtHelper for a year 🎉' : `has been on ArtHelper for ${threshold} years 🎉`;
  }
  if (family === 'likes') return `has ${threshold.toLocaleString()} likes on their art 🎉`;
  if (family === 'views') return `has passed ${threshold.toLocaleString()} views 🎉`;
  const subject = String(metadata?.subject ?? 'something new');
  // "posted their first critique" reads like a critique REQUEST; a critique is feedback
  // they gave on someone's piece — say so.
  if (subject === 'critique') return 'gave their first critique 🎉';
  return `posted their first ${subject} 🎉`;
}

/** How much of a titleless post's body stands in for its missing title. */
const POST_SNIPPET_MAX = 60;

/** An onboarding-series message: a line, a body and a button (#4215). */
export const SERIES_MESSAGE_TYPE = 'series_message';

/** An onboarding-series artist-card item: the digest's cards for one match axis (#4215). */
export const SERIES_SUGGESTIONS_TYPE = 'series_suggestions';

/**
 * The notification types bounties, series items and recurring quests emit. Bell-only in
 * send-notification, and never part of the nightly digest — each one's email is its own
 * checkbox, and a secret bounty has none.
 */
export const BOUNTY_NOTIFICATION_TYPES: readonly string[] = [
  'bounty_available',
  'bounty_earned',
  'quest_period_open',
  SERIES_MESSAGE_TYPE,
  SERIES_SUGGESTIONS_TYPE,
];

/** The title a series item renders, already filled from what its source found. */
export function seriesItemLine(metadata?: Record<string, any> | null): string {
  const title = (metadata?.title as string | undefined) || (metadata?.bountyName as string | undefined);
  return title || 'Something new for you';
}

function bountyPoints(metadata?: Record<string, any> | null): number {
  return Number(metadata?.points ?? 0) || 0;
}

/**
 * "Rate another artist's piece — 50 points". The step's own words — the same line the
 * member reads in a quest — so a bounty never introduces a second vocabulary for one act.
 */
export function bountyAvailableLine(metadata?: Record<string, any> | null): string {
  const what =
    (metadata?.label as string | undefined) || (metadata?.bountyName as string | undefined) || 'A new bounty';
  const points = bountyPoints(metadata);
  return points > 0 ? `${BOUNTY_MARK} ${what} — ${points} points` : `${BOUNTY_MARK} ${what}`;
}

export function bountyEarnedLine(metadata?: Record<string, any> | null): string {
  const name = metadata?.bountyName as string | undefined;
  const points = bountyPoints(metadata);
  const earned = points > 0 ? `You earned ${points} points` : 'You earned a bounty';
  return name ? `${earned} — ${name} 🎉` : `${earned} 🎉`;
}

export function normalizeNotificationType(type?: string | null): string {
  return (type || '').toLowerCase();
}

/** The guild's name when the emitter stamped it; guild rows carry guildId either way. */
function guildName(notification: Pick<Notification, 'metadata'>): string | null {
  const name = notification.metadata?.guildName;
  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

/**
 * "your Thursday Painters guild", not "Thursday Painters".
 *
 * Someone in several guilds reads these rows side by side, and a bare name leaves them
 * working out which of their groups it belongs to. The word "guild" is what makes the
 * name parse as a place rather than a person.
 */
function yourGuild(notification: Pick<Notification, 'metadata'>): string {
  const name = guildName(notification);
  return name ? `your ${name} guild` : 'your guild';
}

/** The names on a guild chat row: "Ana", "Ana and Ben", "Ana, Ben and Cara", "Ana, Ben and 3 others". */
export function guildChatTalkers(notification: Pick<Notification, 'metadata'>): string {
  const senders = Array.isArray(notification.metadata?.senders)
    ? (notification.metadata.senders as Array<{ name?: string }>)
    : [];
  // Newest speaker first: they are who the latest line in the teaser is from.
  const names = senders
    .map((sender) => (typeof sender?.name === 'string' ? sender.name.trim() : ''))
    .filter(Boolean)
    .reverse();
  if (names.length === 0) return 'Your guildmates';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} others`;
}

function guildChatVerb(notification: Pick<Notification, 'metadata'>): string {
  const senders = Array.isArray(notification.metadata?.senders) ? notification.metadata.senders : [];
  return senders.length > 1 ? 'are talking' : 'is talking';
}

function guildChatCount(notification: Pick<Notification, 'metadata'>): string {
  const count = Number(notification.metadata?.messageCount ?? 0);
  return count > 1 ? ` (${count} new messages)` : '';
}

/** Latest line on a guild chat row, named by who said it. */
export function guildChatPreview(senderName: unknown, contentPreview: unknown): string | undefined {
  if (typeof contentPreview !== 'string' || contentPreview.length === 0) return undefined;
  if (typeof senderName !== 'string' || senderName.length === 0) return contentPreview;
  return `${senderName}: ${contentPreview}`;
}

function theGuild(notification: Pick<Notification, 'metadata'>): string {
  const name = guildName(notification);
  return name ? `the ${name} guild` : 'a guild';
}

export function normalizeTargetType(targetType?: string | null): string {
  return (targetType || '').toLowerCase();
}

export function getActorDisplayName(notification: Pick<Notification, 'actor' | 'metadata'>): string {
  const actor = notification.actor;
  if (actor) {
    const fullName = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
    const hydrated = actor.profile?.displayName || actor.username || fullName || actor.profile?.slug;
    if (hydrated) return hydrated;
  }
  // Acknowledgement rows write the sender's name onto the row because hydration
  // often has not reached them yet. The list paints this name next to action
  // text, so a fallback that only lives in generateNotificationMessage never
  // reaches the reader.
  const fromMeta = notification.metadata?.actorName;
  if (typeof fromMeta === 'string' && fromMeta.trim()) return fromMeta.trim();
  // Batched participation writes actorNames / actors, not actorId / actorName.
  // The list paints getActorDisplayName + "and N others", so without this the
  // headline is "Someone and 11 others voted…".
  const fromActorNames = notification.metadata?.actorNames;
  if (Array.isArray(fromActorNames)) {
    const firstName = fromActorNames.find((name) => typeof name === 'string' && name.trim());
    if (typeof firstName === 'string') return firstName.trim();
  }
  const fromActors = notification.metadata?.actors;
  if (Array.isArray(fromActors)) {
    const firstActor = fromActors.find((person) => typeof person?.name === 'string' && person.name.trim());
    if (firstActor && typeof firstActor.name === 'string') return firstActor.name.trim();
  }
  return 'Someone';
}

/**
 * Chips that describe the person who acted: the new-member flag a follow row
 * stores, and the badges on that actor's profile.
 *
 * The list already calls a deleted or missing actor "Someone". That word is
 * `getActorDisplayName` finding no actor — a follow stores no name of its own —
 * so these chips use that same absence. They render only while the actor is
 * still on the row. A live member's chips stay as they are.
 */
export function notificationActorChips(notification: Pick<Notification, 'actor' | 'metadata'>): {
  showNewMember: boolean;
  badges: NotificationActorBadge[];
} {
  const actor = notification.actor;
  if (!actor) return { showNewMember: false, badges: [] };
  return {
    showNewMember: notification.metadata?.isNewMember === true,
    badges: actor.profile?.badges ?? [],
  };
}

function getTargetLabel(targetType: string): string {
  if (targetType === 'post') return 'post';
  if (targetType === 'community_post') return 'post';
  if (targetType === 'comment') return 'comment';
  if (targetType === 'community_comment') return 'comment';
  if (targetType === 'forum_post') return 'forum reply';
  if (targetType === 'image') return 'image';
  if (targetType === 'social_image') return 'post';
  if (targetType === 'video') return 'video';
  return 'content';
}

/**
 * A titleless post's own opening words, standing in for the title it does not have.
 * Trimmed at a word boundary so the line never ends mid-word, and dropped entirely
 * when there is too little left to be worth reading.
 */
function summarizePostBody(snippet?: string | null): string | null {
  const text = (snippet || '').replace(/\s+/g, ' ').trim();
  if (text.length < 8) return null;
  if (text.length <= POST_SNIPPET_MAX) return text;
  const cut = text.slice(0, POST_SNIPPET_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * The reply an `hma_upgrade` acknowledgement produces, keyed on the variant the row was
 * dealt (`metadata.ackVariant`). The person who upgraded receives one of these for every
 * follower who taps, so the pool is what makes their inbox read as a chorus rather than
 * the same line repeated. Order and length must match `labelVariants` on the matching
 * kind in `shared/acknowledgements.ts` — the tapped button and the reply are one variant.
 */
const HMA_SUPPORT_THANKED_REPLIES = [
  'thanked you for supporting human-made art 🙏',
  'loves that you got Human Made Art verified ❤️',
  'cheered your Human Made Art upgrade 🙌',
  'respects your Human Made Art commitment ✊',
];
const HMA_WELCOMED_TO_CLUB_REPLIES = [
  'welcomed you to the human-made artist club! 💪',
  "says you're one of us now 🎉",
  'welcomed you to the verified crew 🖐️',
];

function hmaAcknowledgementReply(type: string, metadata?: Record<string, unknown> | null): string {
  const pool = type === 'welcomed_to_club' ? HMA_WELCOMED_TO_CLUB_REPLIES : HMA_SUPPORT_THANKED_REPLIES;
  const raw = Number(metadata?.ackVariant);
  const idx = Number.isInteger(raw) && raw >= 0 && raw < pool.length ? raw : 0;
  // idx is always in range; the fallback only satisfies noUncheckedIndexedAccess, which
  // widens pool[idx] to string | undefined even though the pools are never empty.
  return pool[idx] ?? 'reacted to your Human Made Art upgrade';
}

/**
 * The subject of a participation line — one actor for the immediate notification, or an
 * aggregated "N people/artists" for the batched one. Mirrors the community_vote_batch
 * phrasing. `count`/`actorNames` come from the batch job; the immediate row has neither
 * and falls back to the single actor.
 */
function participationActors(notification: Notification, pluralNoun = 'people'): string {
  const names = (notification.metadata?.actorNames as string[] | undefined) || [];
  const count = (notification.metadata?.count as number | undefined) ?? (names.length || 1);
  if (count <= 1) {
    return getActorDisplayName(notification);
  }
  if (names.length === 0) {
    return `${count} ${pluralNoun}`;
  }
  const others = count - names.length;
  if (others > 0) {
    return `${names.join(', ')} and ${others} other${others !== 1 ? 's' : ''}`;
  }
  return names.join(', ');
}

/** "<who> <verb> your <noun>" (+ the post title when known). Never leaks the value. */
function participationSentence(notification: Notification, who: string, verb: string, noun: string): string {
  const title = notification.metadata?.postTitle as string | undefined;
  if (title) {
    return `${who} ${verb} your ${noun} "${title}"`;
  }
  return `${who} ${verb} your ${noun}`;
}

const PARTICIPATION_COPY: Record<string, { verb: string; noun: string; plural: string; window: string }> = {
  poll_voted: { verb: 'voted on', noun: 'poll', plural: 'people', window: 'poll' },
  art_rated: { verb: 'rated', noun: 'art', plural: 'artists', window: 'rating window' },
  price_submitted: { verb: 'priced', noun: 'piece', plural: 'people', window: 'pricing window' },
  critique_received: { verb: 'critiqued', noun: 'work', plural: 'people', window: 'critique window' },
};

/**
 * Participation line for the four types, in three shapes: single actor (immediate),
 * aggregated "N people" (batch), and "your window closed — N took part" (window close,
 * flagged by metadata.closed). The value (rating/price) is never included — that is the
 * payoff for clicking through.
 */
function participationMessage(notification: Notification, type: string): string {
  const copy = PARTICIPATION_COPY[type];
  if (!copy) {
    return 'New activity on your post';
  }
  if (notification.metadata?.closed) {
    const count = (notification.metadata?.count as number | undefined) ?? 0;
    const title = notification.metadata?.postTitle as string | undefined;
    const on = title ? ` on "${title}"` : '';
    return `Your ${copy.window} closed — ${count} ${count === 1 ? 'person' : copy.plural} took part${on}`;
  }
  return participationSentence(notification, participationActors(notification, copy.plural), copy.verb, copy.noun);
}

/**
 * Action fragment for a participation row. The list paints `${actorName} ${actionText}`,
 * which is the open-window celebration. A closed window is about the window ending, not
 * a person acting, so this returns null and the list falls through to
 * `generateNotificationMessage` (which owns the closed sentence).
 */
function participationActionText(notification: Notification, type: string): string | null {
  const copy = PARTICIPATION_COPY[type];
  if (!copy || notification.metadata?.closed) {
    return null;
  }
  return `${copy.verb} your ${copy.noun}! 🎉`;
}

/**
 * How many participants BEYOND the named first one an aggregated participation row is
 * about — `count - 1`, or 0 for an immediate (single-participant) row, a window-close
 * row, or a non-participation type. The list paints the first actor's name as a clickable
 * link and appends "and N others" from this, so a batched "12 voted" row does not read as
 * if only the first voter took part.
 */
/**
 * How many people an aggregated row is really about — the number the people-list
 * header compares against the looked-up faces. Participation writes `count`;
 * community_vote_batch writes `voteCount`. Prefer `count` so a capped actors list
 * (5 faces, 12 took part) does not collapse the header to "5 people".
 */
export function aggregatedPeopleTotal(metadata: Record<string, unknown> | null | undefined, shown: number): number {
  const raw = metadata?.count ?? metadata?.voteCount ?? shown;
  return Number(raw) || shown;
}

/** People-list header: "Showing 5 of 12" when the looked-up faces are a cap, else "N people". */
export function peopleListCountLabel(shown: number, total: number): string {
  if (total > shown) return `Showing ${shown} of ${total}`;
  return `${total} ${total === 1 ? 'person' : 'people'}`;
}

export function participationOthersCount(notification: Pick<Notification, 'type' | 'metadata'>): number {
  const type = normalizeNotificationType(notification.type);
  if (!PARTICIPATION_COPY[type]) {
    return 0;
  }
  if (notification.metadata?.closed) {
    return 0;
  }
  const count = (notification.metadata?.count as number | undefined) ?? 0;
  return count > 1 ? count - 1 : 0;
}

/**
 * "upvoted your post", or "and 2 others upvoted your comment" once more than one person
 * has. The list paints the newest voter's name in front of this, so the count here is
 * everyone else. `targetType` is the normalised one (`communitypost` / `communitycomment`).
 */
function communityVoteActionText(notification: Notification, targetType: string): string {
  const noun = targetType === 'communitycomment' ? 'comment' : 'post';
  const others = (Number(notification.metadata?.voteCount) || 1) - 1;
  if (others <= 0) return `upvoted your ${noun}`;
  return `and ${others} other${others === 1 ? '' : 's'} upvoted your ${noun}`;
}

export function generateNotificationMessage(notification: Notification): string {
  const type = normalizeNotificationType(notification.type);
  const targetType = normalizeTargetType(notification.targetType);
  const actorName = getActorDisplayName(notification);
  const targetLabel = getTargetLabel(targetType);

  switch (type) {
    case 'like':
      return `${actorName} liked your ${targetLabel}`;
    case 'comment':
      return `${actorName} commented on your ${targetLabel}`;
    case 'comment_reply':
      return `${actorName} replied to your comment`;
    case 'share':
      return `${actorName} shared your ${targetLabel}`;
    case 'follow':
      return `${actorName} followed you`;
    case 'possible_new_friends': {
      // A daily introduction of new artists to an active member (#4479). The row draws
      // each person itself; this sentence is for the toast and any plain-text surface.
      const count = Array.isArray(notification.metadata?.newcomers) ? notification.metadata.newcomers.length : 1;
      return count > 1
        ? `${actorName} and ${count - 1} other new artist${count - 1 === 1 ? '' : 's'} might be your new friends`
        : `${actorName} just joined and might be your new friend`;
    }
    case 'forum_reply':
      return `${actorName} replied to your forum topic`;
    case 'forum_post_reply':
      return `${actorName} replied to your forum comment`;
    case 'community_invite':
    case 'community_restricted_invite': {
      const communityName = notification.metadata?.communityName;
      const communityText = communityName ? `the ${communityName} community` : 'a community';
      const role = notification.metadata?.role;
      const roleText = role && role !== 'member' ? ` as a ${role}` : '';
      return `You have been invited to join ${communityText}${roleText}`;
    }
    case 'mention': // legacy alias — kept for any pre-existing notification records
    case 'community_mention': {
      const postTitle = notification.metadata?.communityPostTitle as string | undefined;
      return postTitle
        ? `${actorName} mentioned you on "${postTitle}"`
        : `${actorName} mentioned you in a community post`;
    }
    case 'community_reply': {
      // The post title is on the row already, and without it the line says only
      // that SOMETHING was replied to — the reader cannot tell which of their
      // posts it was about.
      const replyPostTitle = notification.metadata?.postTitle as string | undefined;
      const on = replyPostTitle ? ` on "${replyPostTitle}"` : '';
      return notification.metadata?.isReply
        ? `${actorName} replied to your comment${on}`
        : `${actorName} commented on your post${replyPostTitle ? ` "${replyPostTitle}"` : ''}`;
    }
    case 'poll_voted':
    case 'art_rated':
    case 'price_submitted':
    case 'critique_received':
      return participationMessage(notification, type);
    case 'critique_valued': {
      const valuedTitle = notification.metadata?.postTitle as string | undefined;
      return valuedTitle
        ? `${actorName} marked your critique of "${valuedTitle}" as helpful`
        : `${actorName} marked your critique as helpful`;
    }
    case 'critique_revision': {
      const revisedTitle = notification.metadata?.postTitle as string | undefined;
      return revisedTitle
        ? `${actorName} posted a new version of "${revisedTitle}"`
        : `${actorName} posted a new version of a piece you critiqued`;
    }
    case 'feedback_thanked':
      return `${actorName} thanked you for your feedback 🙏`;
    case 'artwork_published': {
      const artist =
        actorName !== 'Someone' ? actorName : (notification.metadata?.artistName as string | undefined) || 'Someone';
      const pieces = Number(notification.metadata?.imageCount ?? 1) || 1;
      if (pieces > 1) return `${artist} added ${pieces} new artworks today`;
      // A single piece can name itself, which is far more inviting than a count of one.
      const only = notification.metadata?.leadImageTitle as string | undefined;
      return only ? `${artist} added a new piece — "${only}"` : `${artist} added a new piece`;
    }
    case 'referral_joined': {
      const friendName = (notification.metadata?.friendName as string | undefined) || 'A friend';
      const points = (notification.metadata?.points as number | undefined) ?? 0;
      // Under a subscription-only mechanism the friend got a gifted subscription, not
      // points, and a lapsed gift invite ('none') gave the friend nothing — so "you both
      // earned" would be wrong. The referrer still did.
      const friendReward = notification.metadata?.friendReward as string | undefined;
      if (friendReward === 'subscription' || friendReward === 'none') {
        return points > 0
          ? `${friendName} joined from your invite — you earned ${points} points 🎁`
          : `${friendName} joined from your invite`;
      }
      return points > 0
        ? `${friendName} joined from your invite — you both earned ${points} points 🎁`
        : `${friendName} joined from your invite`;
    }
    case 'referral_gift': {
      const giver = (notification.metadata?.actorName as string | undefined) || 'The friend who invited you';
      const gift = (notification.metadata?.points as number | undefined) ?? 0;
      return gift > 0
        ? `${giver} invited you to ArtHelper — here are ${gift} points to start with 🎁`
        : `${giver} invited you to ArtHelper`;
    }
    case 'referral_gift_subscription': {
      const giver = (notification.metadata?.actorName as string | undefined) || 'The friend who invited you';
      const months = notification.metadata?.termMonths as number | undefined;
      return months
        ? `${giver} gifted you ${giftTermLabel(months)} of Human Made Art verification 🎁`
        : `${giver} gifted you Human Made Art verification 🎁`;
    }
    case 'access_granted':
      return "You're in — your invites opened the doors to ArtHelper 🎉";
    case 'welcomed_aboard': {
      const welcomer =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      return `${welcomer} welcomed you aboard 👋`;
    }
    case 'invite_appreciated': {
      const thanker =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      return `${thanker} says thank you so much! 🙏`;
    }
    case 'quest_completed': {
      const questActor =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      const questName = notification.metadata?.questName as string | undefined;
      return questName ? `${questActor} completed the quest "${questName}"` : `${questActor} completed a quest`;
    }
    case 'quest_congratulated': {
      const questActor =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      const questName = notification.metadata?.questName as string | undefined;
      return questName
        ? `${questActor} congratulated you on finishing "${questName}" 🎉`
        : `${questActor} congratulated you on finishing a quest 🎉`;
    }
    case 'quest_joined': {
      const questActor =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      const questName = notification.metadata?.questName as string | undefined;
      return questName ? `${questActor} joined the quest "${questName}"` : `${questActor} joined a quest`;
    }
    // Bounty rows are the app speaking, not a person: no actor, a whole sentence here,
    // and null from getNotificationActionText so the list shows this line as is.
    case 'bounty_available':
      return bountyAvailableLine(notification.metadata);
    case 'bounty_earned':
      return bountyEarnedLine(notification.metadata);
    case SERIES_MESSAGE_TYPE:
    case SERIES_SUGGESTIONS_TYPE:
      return seriesItemLine(notification.metadata);
    case 'quest_period_open': {
      const questName = notification.metadata?.questName as string | undefined;
      return questName
        ? `"${questName}" is open again — pick it back up`
        : 'Your quest is open again — pick it back up';
    }
    case 'community_follow_post': {
      const followActorName =
        actorName !== 'Someone' ? actorName : (notification.metadata?.authorName as string | undefined) || 'Someone';
      const followPostTitle = notification.metadata?.postTitle as string | undefined;
      const followCommunityName = notification.metadata?.communityName as string | undefined;
      if (followPostTitle && followCommunityName)
        return `${followActorName} posted "${followPostTitle}" in ${followCommunityName}`;
      if (followPostTitle) return `${followActorName} posted "${followPostTitle}"`;
      // ~7% of these rows carry no title (a post that opens straight into body
      // text). "created a new post" told the reader nothing at all; the post's own
      // opening words, then the community, are the most specific true things left.
      const followSnippet = summarizePostBody(notification.metadata?.contentSnippet as string | undefined);
      if (followSnippet) return `${followActorName} posted “${followSnippet}”`;
      if (followCommunityName) return `${followActorName} posted in ${followCommunityName}`;
      return `${followActorName} shared something new`;
    }
    case 'community_vote_batch': {
      const voterNames = (notification.metadata?.voterNames as string[] | undefined) || [];
      const voteCount = (notification.metadata?.voteCount as number | undefined) || 0;
      const batchPostTitle = notification.metadata?.postTitle as string | undefined;
      const others = voteCount - voterNames.length;
      const who =
        voterNames.length === 0
          ? peopleListCountLabel(voteCount, voteCount)
          : others > 0
            ? `${voterNames.join(', ')} and ${others} other${others !== 1 ? 's' : ''}`
            : voterNames.join(', ');
      return batchPostTitle ? `${who} upvoted your post "${batchPostTitle}"` : `${who} upvoted your post`;
    }
    case 'community_trending_alert': {
      const trendingPostTitle = notification.metadata?.postTitle as string | undefined;
      const isTop10 = notification.metadata?.isTop10 as boolean | undefined;
      if (trendingPostTitle) {
        return isTop10
          ? `Your post "${trendingPostTitle}" made the top 10 trending`
          : `Your post "${trendingPostTitle}" is trending`;
      }
      return 'Your post is trending';
    }
    case 'recommended_connections': {
      const suggested = Array.isArray(notification.metadata?.recommendationIds)
        ? notification.metadata.recommendationIds.length
        : 0;
      const named = notification.relatedActors?.length
        ? notification.relatedActors.map((a) => getActorDisplayName({ actor: a })).filter((n) => n !== 'Someone')
        : [];
      // The row says what the subject says, in the same axis vocabulary. These
      // are read in one glance — a subject promising coffee over a row that
      // says "you may want to know" undoes the subject's work, and the reader
      // lands on a `/network` section headed with these same words.
      const framing = SUGGESTED_CONNECTION_EMAIL_FRAMINGS[suggestedConnectionAxisFromMetadata(notification.metadata)];
      if (named.length === 1) return framing.named[0]!(named[0]!, 1);
      if (named.length > 1) {
        const others = (suggested || named.length) - 1;
        return framing.named[0]!(`${named[0]} and ${others} other${others === 1 ? '' : 's'}`, named.length);
      }
      // Nothing hydrated and no ids to count: say so, rather than inventing a
      // number. "1 artist who resonates with your work" over a row whose
      // contents we could not read is a fabrication the old copy avoided too.
      if (suggested === 0) return `New ${framing.label}`;
      // Agrees with the ARTISTS in the row, never the row count.
      return framing.counted[0]!(suggested);
    }
    case 'community_digest': {
      const postCount = notification.metadata?.postCount as number | undefined;
      return postCount
        ? `You have ${postCount} trending post${postCount !== 1 ? 's' : ''} to catch up on`
        : "Check out what's trending in your communities";
    }
    case 'community_invite_accepted':
      return `${actorName} accepted your invite to ${notification.metadata?.communityName || 'your community'}`;
    // The reply to that invite, coming back the other way. It names the community
    // because the same pair can be welcomed into more than one.
    case 'community_welcomed': {
      // Same fallback as every other acknowledgement line: the service writes the
      // sender's name into the row precisely because the actor is often not hydrated
      // here, and reading only the join renders a real welcome as "Someone".
      const welcomer =
        actorName !== 'Someone' ? actorName : (notification.metadata?.actorName as string | undefined) || 'Someone';
      return `${welcomer} welcomed you to ${notification.metadata?.communityName || 'the community'} 👋`;
    }
    case 'milestone_reached':
      return milestoneLine(notification.metadata);
    case 'connection_milestone': {
      const name =
        actorName !== 'Someone'
          ? actorName
          : (notification.metadata?.actorName as string | undefined) || 'A connection';
      return `${name} ${connectionMilestoneReason(notification.metadata)}`;
    }
    case 'milestone_congratulated':
      return `${actorName} congratulated you 🎉`;
    // The sender's own words, in quotes. That IS the finding: the identical line lands
    // differently when a person pressed the button rather than the app.
    case 'wip_requested':
      return `${actorName} asks: "Whatcha working on?"`;
    case 'wip_shared':
      return `${actorName} posted ${notification.metadata?.postTitle ? `"${notification.metadata.postTitle}"` : 'something new'} after you asked`;
    case 'weekly_reach': {
      const views = Number(notification.metadata?.views ?? 0);
      const followers = Number(notification.metadata?.newFollowers ?? 0);
      const seen = `Your work was seen ${views.toLocaleString()} times this week`;
      // The followers half is dropped rather than written as "and 0 new people", which
      // reads as a reproach for something the artist did not do.
      return followers > 0
        ? `${seen}, and ${followers} new ${followers === 1 ? 'person' : 'people'} started following you`
        : seen;
    }
    case 'community_vote':
      return `${actorName} ${communityVoteActionText(notification, targetType)}`;
    case 'COMMUNITY_REACTION':
    case 'community_reaction': {
      // Every emoji they hold, so a row that has been resurfaced twice reads as what
      // they actually chose rather than what they chose first.
      const held = notification.metadata?.emojis;
      const shown = Array.isArray(held) && held.length > 0 ? held.join('') : notification.metadata?.emoji || '❤️';
      return `${actorName} reacted ${shown} to your work`;
    }
    case 'post_welcomed':
      return `${actorName} welcomed you to ArtHelper 👋`;
    case 'greeted':
      return `${actorName} said hi 👋`;
    case 'greeted_back':
      return `${actorName} said hi back 👋`;
    case 'connection_excited':
      return `${actorName} is excited to connect 🎉`;
    // The two thank-yous. Both name what is being thanked for, because "X thanked you"
    // on its own leaves the reader working out which of the things they did it was.
    case 'reaction_thanked':
      return `${actorName} thanked you for your reaction 🙏`;
    case 'comment_thanked':
      return `${actorName} thanked you for your comment 🙏`;
    case 'reply_thanked':
      return `${actorName} thanked you for reading 🙏`;
    case 'welcome_thanked':
      return `${actorName} thanked you for the welcome 🙏`;
    case 'community_role_changed':
      return `Your role in ${notification.metadata?.communityName || 'a community'} has been changed to ${notification.metadata?.newRole || 'a new role'}`;
    case 'community_flagged':
      return 'Content in your community has been flagged for review';
    case 'content_flagged': {
      const reasons = notification.metadata?.reasons as Array<{ scanType: string; reason: string }> | undefined;
      // Use the pre-computed summary `reason` field; it already says "multiple policy violations"
      // when multiple scan types flagged the same content.
      const reason = notification.metadata?.reason || reasons?.[0]?.reason || 'a policy review';
      const postTitle = notification.metadata?.postTitle as string | undefined;
      const communitySlug = notification.metadata?.communitySlug as string | undefined;
      const isComment = targetType === 'community_comment' || targetType === 'comment';
      const isCommentImage = notification.metadata?.isCommentImage as boolean | undefined;
      if ((isComment || isCommentImage) && postTitle) {
        return `Your comment on post "${postTitle}" has been flagged for review for ${reason}`;
      }
      // If postTitle is set, it's a post. If communitySlug is set without a postTitle,
      // it's still post-related. Fall back to targetLabel only for standalone images.
      const flaggedLabel = postTitle || communitySlug ? 'post' : targetLabel;
      if (postTitle) {
        return `Your ${flaggedLabel} "${postTitle}" has been flagged for review for ${reason}`;
      }
      return `Your ${flaggedLabel} has been flagged for review for ${reason}`;
    }
    case 'content_approved': {
      const approvedPostTitle = notification.metadata?.postTitle as string | undefined;
      const isComment = targetType === 'community_comment' || targetType === 'comment';
      const isCommentImage = notification.metadata?.isCommentImage as boolean | undefined;
      if ((isComment || isCommentImage) && approvedPostTitle) {
        return `Your comment on post "${approvedPostTitle}" has been approved`;
      }
      if (approvedPostTitle) {
        return `Your post "${approvedPostTitle}" has been approved`;
      }
      return `Your ${targetLabel} has been approved`;
    }
    case 'content_review_completed': {
      const reviewMessage = notification.metadata?.message as string | undefined;
      if (reviewMessage) {
        return `Your content review request has been completed: ${reviewMessage}`;
      }
      return 'Your content review request has been completed';
    }
    case 'system':
      return (notification.metadata?.message as string) || 'System notification';
    case 'dm_received': {
      // Resurface increments `metadata.messageCount`; older rows that
      // pre-date the counter just say "sent you a message".
      const isGroup = Boolean(notification.metadata?.isGroup);
      const messageCount = Number(notification.metadata?.messageCount ?? 1);
      const verb = isGroup ? 'sent a message in your group thread' : 'sent you a message';
      if (messageCount > 1) {
        return `${actorName} ${verb} (${messageCount} new)`;
      }
      return `${actorName} ${verb}`;
    }
    case 'connection_request':
      return `${actorName} sent you a connection request`;
    case 'connection_accepted':
      return `${actorName} accepted your connection request`;
    case 'recommendation_received':
      return `${actorName} wrote you a recommendation`;
    case 'recommendation_approved':
      return `${actorName} approved your recommendation`;
    case 'recommendation_declined':
      return `${actorName} declined your recommendation`;
    case 'spark_review_ready': {
      const sparkImageTitle = notification.metadata?.imageTitle as string | undefined;
      if (sparkImageTitle) {
        return `Arty reviewed “${sparkImageTitle}”. Your honest review is ready.`;
      }
      return 'Arty reviewed your first piece. Your honest review is ready.';
    }
    case 'critique_posted':
      return 'Your critique post is live — expect feedback within a few hours.';
    case 'hma_upgrade':
      return `${actorName} upgraded to Human Made Art verification`;
    case 'support_thanked':
    case 'welcomed_to_club':
      return `${actorName} ${hmaAcknowledgementReply(type, notification.metadata)}`;
    case 'high_fived':
      return `${actorName} high-fived you back 🙌`;
    // Guild rows. Without these every one of them fell through to the generic
    // "New notification" — the same fallback this file's spec records burning 86,076
    // users' suggestions. The guild name is in metadata for the rows that read better
    // with it; the actor carries the rest.
    case 'guild_work_shared':
      return `${actorName} shared new work with ${yourGuild(notification)}`;
    case 'guild_reaction':
      return `${actorName} reacted to your work`;
    case 'guild_member_posted_publicly':
      return `${actorName} posted publicly`;
    case 'guild_invite':
      return `${actorName} invited you to ${theGuild(notification)}`;
    // The applicant's answer. "You are in" leads, because that is the whole content of
    // the row — who approved it is the least interesting part to the person waiting.
    case 'guild_application_approved':
      return `You are in — ${actorName} approved your request to join ${theGuild(notification)}`;
    case 'guild_quest_started':
      return `${actorName} started a quest`;
    case 'guild_quest_invite':
      return `${actorName} invited ${yourGuild(notification)} to a quest`;
    case 'guild_member_milestone':
      return `${actorName} hit a milestone`;
    // The room's running summary. Names lead — who is talking is the whole reason to look.
    case 'guild_chat':
      return `${guildChatTalkers(notification)} ${guildChatVerb(notification)} in ${theGuild(notification)}${guildChatCount(notification)}`;
    // Built by the guild's activity tab from the membership itself, never fanned out.
    case 'guild_member_joined':
      return `${actorName} joined ${yourGuild(notification)}`;
    case 'guild_started':
      return `${actorName} started ${yourGuild(notification)}`;
    default:
      return 'New notification';
  }
}

export function getNotificationActionText(notification: Notification): string | null {
  const type = normalizeNotificationType(notification.type);
  const targetType = normalizeTargetType(notification.targetType);
  const targetLabel = getTargetLabel(targetType);
  const postTitle = notification.metadata?.postTitle as string | undefined;

  switch (type) {
    case 'like':
      return `liked your ${targetLabel}`;
    case 'comment':
      return `commented on your ${targetLabel}`;
    case 'comment_reply':
      return 'replied to your comment';
    case 'share':
      return `shared your ${targetLabel}`;
    case 'follow':
      return 'followed you';
    case 'forum_reply':
      return 'replied to your forum topic';
    case 'forum_post_reply':
      return 'replied to your forum comment';
    case 'mention':
    case 'community_mention': {
      const mentionPostTitle = notification.metadata?.communityPostTitle as string | undefined;
      return mentionPostTitle ? `mentioned you on "${mentionPostTitle}"` : 'mentioned you';
    }
    case 'community_reply':
      if (notification.metadata?.isReply) return 'replied to your comment';
      return postTitle ? `commented on ${postTitle}` : 'commented on your community post';
    // Participation is good news — someone engaged with your work — so an OPEN window
    // carries a bit of celebration (never the value itself). A closed row returns null
    // so the list uses generateNotificationMessage's "your window closed" sentence.
    case 'poll_voted':
    case 'art_rated':
    case 'price_submitted':
    case 'critique_received':
      return participationActionText(notification, type);
    case 'critique_valued':
      return 'marked your critique as helpful';
    case 'critique_revision':
      return 'posted a new version of a piece you critiqued';
    case 'feedback_thanked':
      return 'thanked you for your feedback 🙏';
    case 'artwork_published': {
      const pieces = Number(notification.metadata?.imageCount ?? 1) || 1;
      if (pieces > 1) return `added ${pieces} new artworks today`;
      const only = notification.metadata?.leadImageTitle as string | undefined;
      return only ? `added a new piece — "${only}"` : 'added a new piece';
    }
    case 'quest_completed': {
      const questName = notification.metadata?.questName as string | undefined;
      return questName ? `completed the quest "${questName}"` : 'completed a quest';
    }
    case 'quest_congratulated': {
      const questName = notification.metadata?.questName as string | undefined;
      return questName
        ? `congratulated you on finishing "${questName}" 🎉`
        : 'congratulated you on finishing a quest 🎉';
    }
    case 'quest_joined': {
      const questName = notification.metadata?.questName as string | undefined;
      return questName ? `joined the quest "${questName}"` : 'joined a quest';
    }
    case 'bounty_available':
    case 'bounty_earned':
    case 'quest_period_open':
    case SERIES_MESSAGE_TYPE:
    case SERIES_SUGGESTIONS_TYPE:
      return null;
    case 'community_follow_post': {
      const followPostTitle = notification.metadata?.postTitle as string | undefined;
      const followCommunityName = notification.metadata?.communityName as string | undefined;
      if (followPostTitle && followCommunityName) return `posted "${followPostTitle}" in ${followCommunityName}`;
      if (followPostTitle) return `posted "${followPostTitle}"`;
      // Titleless posts (a post that opens straight into body text) are the only rows
      // that reach here. "created a new post" told the reader nothing; the community —
      // or the post's own opening words — is the most specific true thing left to say.
      const followSnippet = summarizePostBody(notification.metadata?.contentSnippet as string | undefined);
      if (followSnippet) return `posted “${followSnippet}”`;
      if (followCommunityName) return `posted in ${followCommunityName}`;
      return 'shared something new';
    }
    case 'community_invite_accepted':
      return `accepted your invite to ${notification.metadata?.communityName || 'your community'}`;
    case 'community_welcomed':
      return `welcomed you to ${notification.metadata?.communityName || 'the community'} 👋`;
    case 'connection_milestone':
      return connectionMilestoneReason(notification.metadata);
    case 'milestone_congratulated':
      return 'congratulated you 🎉';
    case 'wip_requested':
      return 'asks: "Whatcha working on?"';
    case 'wip_shared':
      return 'posted something new after you asked';
    case 'community_vote':
      return communityVoteActionText(notification, targetType);
    case 'COMMUNITY_REACTION':
    case 'community_reaction': {
      const held = notification.metadata?.emojis;
      const shown = Array.isArray(held) && held.length > 0 ? held.join('') : notification.metadata?.emoji || '❤️';
      return `reacted ${shown} to your work`;
    }
    case 'post_welcomed':
      return 'welcomed you to ArtHelper 👋';
    case 'greeted':
      return 'said hi 👋';
    case 'greeted_back':
      return 'said hi back 👋';
    case 'connection_excited':
      return 'is excited to connect 🎉';
    case 'reaction_thanked':
      return 'thanked you for your reaction 🙏';
    case 'comment_thanked':
      return 'thanked you for your comment 🙏';
    case 'reply_thanked':
      return 'thanked you for reading 🙏';
    case 'welcome_thanked':
      return 'thanked you for the welcome 🙏';
    case 'dm_received': {
      const isGroup = Boolean(notification.metadata?.isGroup);
      const messageCount = Number(notification.metadata?.messageCount ?? 1);
      const verb = isGroup ? 'sent a message in your group thread' : 'sent you a message';
      return messageCount > 1 ? `${verb} (${messageCount} new)` : verb;
    }
    case 'connection_request':
      return 'sent you a connection request';
    case 'connection_accepted':
      return 'accepted your connection request';
    case 'recommendation_received':
      return 'wrote you a recommendation';
    case 'recommendation_approved':
      return 'approved your recommendation';
    case 'recommendation_declined':
      return 'declined your recommendation';
    case 'spark_review_ready':
      return null;
    case 'critique_posted':
      return null;
    case 'hma_upgrade':
      return 'upgraded to Human Made Art verification';
    case 'support_thanked':
    case 'welcomed_to_club':
      return hmaAcknowledgementReply(type, notification.metadata);
    case 'high_fived':
      return 'high-fived you back 🙌';
    default:
      return null;
  }
}

/**
 * Effective sort/group timestamp for a notification. Prefers `updatedAt` so
 * resurfaced rows (read → unread on a new ping for the same dedup key) move
 * out of stale time-buckets like "Last 7 days" into "Today". Falls back to
 * `createdAt` for legacy payloads/caches that haven't fetched `updatedAt`.
 */
export function getNotificationTimestamp(notification: Pick<Notification, 'createdAt' | 'updatedAt'>): string {
  return notification.updatedAt || notification.createdAt;
}

export function groupNotificationsByTime(notifications: Notification[]): NotificationGroups {
  const now = new Date();
  const sevenDaysAgo = subDays(now, 7);
  const yesterday = subDays(now, 1);
  const thirtyDaysAgo = subDays(now, 30);

  const groups: NotificationGroups = {
    today: [],
    yesterday: [],
    last7Days: [],
    last30Days: [],
    older: [],
  };

  notifications.forEach((n) => {
    const d = new Date(getNotificationTimestamp(n));

    // A legacy cache row can carry no usable timestamp at all. Every window test below
    // answers false for an Invalid Date, which would present it as this month's activity;
    // it belongs at the bottom of the list instead.
    if (Number.isNaN(d.getTime())) {
      groups.older.push(n);
      return;
    }

    if (isToday(d)) {
      groups.today.push(n);
      return;
    }

    if (isYesterday(d)) {
      groups.yesterday.push(n);
      return;
    }

    if (isWithinInterval(d, { start: sevenDaysAgo, end: yesterday })) {
      groups.last7Days.push(n);
      return;
    }

    if (d < thirtyDaysAgo) {
      groups.older.push(n);
      return;
    }

    groups.last30Days.push(n);
  });

  return groups;
}

/**
 * How long an account reads as new.
 *
 * Two weeks is roughly the window in which a welcome still lands as a welcome rather than
 * as a stranger commenting on how long you have been here.
 */
export const NEW_MEMBER_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Did this account arrive recently enough that a greeting is the obvious reply? `windowMs`
 * narrows or widens "recently" for a caller with its own definition of new.
 */
export function isNewAccount(
  createdAt: Date | string | null | undefined,
  now: number = Date.now(),
  windowMs: number = NEW_MEMBER_WINDOW_MS
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return now - created < windowMs;
}

/**
 * How recently an account must have joined for its connection requests to earn the
 * nudges (reminder email, accept link, banner) and to count in the time-to-accept goal.
 *
 * Narrower than `NEW_MEMBER_WINDOW_MS` on purpose: the nudges target the first week, when
 * a same-day accept still changes whether the newcomer comes back.
 */
export const CONNECTION_NUDGE_NEW_MEMBER_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Had this account joined within the nudge window as of `at` (usually the request's createdAt)? */
export function isConnectionNudgeNewMember(
  createdAt: Date | string | null | undefined,
  at: Date | string | number = Date.now()
): boolean {
  const reference = new Date(at).getTime();
  if (Number.isNaN(reference)) {
    return false;
  }
  return isNewAccount(createdAt, reference, CONNECTION_NUDGE_NEW_MEMBER_WINDOW_MS);
}

/**
 * How long a member's engagement reaches them instantly rather than in a roundup (#4096).
 * Day 0 to day 1 is the retention cliff, so the first 5 days are when a person-to-person
 * signal is worth the interruption.
 */
export const NEW_MEMBER_INSTANT_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;

/**
 * Was this account at most 5 days old at `at`? Inclusive at exactly 5 days.
 *
 * `at` is the moment of the event (a vote's `updatedAt`), not when a job runs, so the
 * instant path and the hourly roundup classify the same vote the same way.
 */
export function isInstantEngagementRecipient(
  createdAt: Date | string | null | undefined,
  at: Date | number = Date.now()
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return new Date(at).getTime() - created <= NEW_MEMBER_INSTANT_WINDOW_MS;
}

/**
 * A quest notification, split so the QUEST NAME can be rendered as a link rather than
 * baked into a sentence (Nick, 2026-09-02).
 *
 * The parts live beside the plain-string builders instead of the renderer so the two
 * cannot drift into saying different things: `getNotificationActionText` still produces
 * the flat sentence for surfaces that cannot render a link (the client app, email), and
 * this returns the same words with a seam in the middle.
 *
 * Returns null for everything that is not a quest notification, which is the caller's
 * signal to fall back to the flat string.
 */
export function getQuestNotificationParts(
  notification: Notification
): { prefix: string; questName: string; questSlug: string; suffix: string } | null {
  const type = normalizeNotificationType(notification.type);
  const questName = notification.metadata?.questName as string | undefined;
  const questSlug = notification.metadata?.questSlug as string | undefined;
  // Without a name there is nothing to link, and without a slug there is nowhere to
  // link it — either way the flat sentence is the honest render.
  if (!questName || !questSlug) return null;

  if (type === 'quest_completed') return { prefix: 'completed the quest', questName, questSlug, suffix: '' };
  if (type === 'quest_joined') return { prefix: 'joined the quest', questName, questSlug, suffix: '' };
  if (type === 'quest_congratulated') {
    return { prefix: 'congratulated you on finishing', questName, questSlug, suffix: ' \u{1F389}' };
  }
  return null;
}
